<template>
  <div>
    <div class="row">
      <div class="col-6 d-flex align-items-center justify-content-lg-start">
        <Button
          v-if="showBackButton && actionType === 'save' || 'add'"
          :label="$t('List page')"
          size="md"
          icon="ArrowLeftIcon"
          show-icon
          class="mb-1"
          @click="backNavigationAction"
        />
      </div>
      <div class="col-6 d-flex justify-content-end">
        <Button
          :label="$t('Search')"
          size="md"
          icon="SearchIcon"
          :class="searchButtonClass"
          show-icon
          @click="showSearchField"
        />
      </div>
    </div>

    <form-wizard
      ref="pfiLending"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      :next-button-text="$t('Next')"

      class="wizard-vertical mb-3"
    >

      <tab-content
        :title="$t('PFI Information')"
        :before-change="validateLoanService"
      >
        <validation-observer ref="loanService">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2 mt-2"
              >
                <h5 class="mb-0">
                  {{ $t('PFI Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="pfi-code"
                  v-model="pfiCustomerFormData.partnerCode"
                  :label="$t('PFI Code')"
                  :reduce="option => option.value"
                  :options="pfiPartnerOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('PFI Code')"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="getLinkedPfiByPartnerCode"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="pfi-name"
                  v-model="pfiCustomerFormData.partnerName"
                  :label="$t('PFI Name')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="pfi-state"
                  v-model="pfiCustomerFormData.state"
                  :label="$t('PFI State')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="pfi-contact-email-address"
                  v-model="pfiCustomerFormData.contactEmailAddress"
                  :label="$t('PFI Contact Email Address')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="pfi-phone-number"
                  v-model="pfiCustomerFormData.contactPhoneNumber"
                  :label="$t('PFI Phone Number')"
                  type="text"
                  readonly
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Loan Disbursement Details -->
      <tab-content
        :title="$t('Loan Disbursement Details')"
        :before-change="validateLoanDisbursement"
      >
        <validation-observer ref="loanDisbursementRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Loan Disbursement Details') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="bank-code"
                  v-model="pfiLendingFormData.externalBankName"
                  label="Bank Account Name"
                  :options="bankListOptions"
                  placeholder="Select"
                  :reduce="option => option.label"
                  item="label"
                  rules="required"
                  error-name="Bank Account Name"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="bank-account-number"
                  v-model="pfiLendingFormData.externalDisbursementAccount"
                  label="Bank Account Number"
                  type="number"
                  rules="required|min: 10|max: 10"
                  error-name="Bank Account Number"
                  placeholder="Enter bank account number"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Loan Account Information -->
      <tab-content
        :title="$t('Loan Account Info')"
        :before-change="validateLoanAccountInfo"
      >
        <validation-observer ref="loanAccountInfo">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Loan Account Information') }}
                </h5>
              </b-col>

              <b-col md="6">
                <InputField
                  id="loan-pfi-name"
                  v-model="pfiCustomerFormData.partnerName"
                  label="PFI Name"
                  type="text"
                  error-name="PFI Name"
                  rules="required"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="purpose-loan-application"
                  v-model="pfiLendingFormData.description"
                  label="Purpose of Loan Application"
                  type="text"
                  error-name="Purpose of Loan Application"
                  rules="required"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="loan-product"
                  v-model="pfiLendingFormData.externalProductCode"
                  label="Loan Product"
                  :reduce="option => option.value"
                  :options="loanProductOptions"
                  placeholder="Select"
                  error-name="Loan Product"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="setLoanFee"
                />
              </b-col>
              <b-col md="6">
                <InputNumberField
                  id="loan-principal-amount"
                  v-model="pfiLendingFormData.amount"
                  label="Loan Principal Amount"
                  type="text"
                  error-name="Loan Principal Amount"
                  rules="required"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mt-3 mb-2"
              >
                <h5>
                  {{ $t('Principal Repayment Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="principal-repayment"
                  v-model="pfiLendingFormData.principalRepaymentType"
                  label="Principal Repayment Option"
                  :reduce="option => option.value"
                  :options="repaymentTypeOptions"
                  placeholder="Select"
                  error-name="Principal Repayment Option"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <InputField
                  id="principal-to-be-repaid"
                  v-model.number="pfiLendingFormData.principalToBeRepaidEvery"
                  :label="$t('Principal To Be Repaid Every')"
                  type="number"
                  :error-name="$t('Principal To Be Repaid Every')"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                  :on-input="computeTenure1"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="principal-repayment-frequency"
                  v-model="pfiLendingFormData.principalFrequency"
                  label="Principal Repayment Frequency"
                  :reduce="option => option.value"
                  :options="repaymentFrequencyOptions"
                  placeholder="Select"
                  error-name="Principal Repayment Frequency"
                  rules="required"
                  item="label"
                  :set-selected="computeTenure2"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="principal-installment"
                  v-model.number="pfiLendingFormData.principalInstallment"
                  :label="$t('Over')"
                  type="number"
                  :error-name="$t('Principal Installment')"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                  :on-input="computeTenure3"
                  text="Installments"
                />
              </b-col>
              <b-col md="6">
                <DatePickerField
                  id="start-date"
                  v-model="pfiLendingFormData.effectiveDate"
                  rules="required"
                  :label="$t('Loan Start Date')"
                  :error-name="$t('Loan Start Date')"
                  :config="datePickerConfig"
                  :placeholder="$t('Select Loan Start Date')"
                  :disabled="isReadonly"
                  @input="setEndDate"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="tenure"
                  v-model.number="pfiLendingFormData.tenure"
                  label="Tenure"
                  type="number"
                  error-name="Tenure"
                  rules="required"
                  :readonly="isReadonly"
                  @input="setEndDate"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="maturity-date"
                  v-model="pfiLendingFormData.maturityDate"
                  label="Maturity Date"
                  type="text"
                  rules="required"
                  error-name="Maturity Date"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col
                cols="12"
                class="mt-3 mb-2"
              >
                <h5>
                  {{ $t('Interest Repayment Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <InputField
                  id="applicable-interest-rate"
                  v-model.number="pfiLendingFormData.interestRate"
                  label="Applicable Interest Rate"
                  type="number"
                  rules="required"
                  error-name="Applicable Interest Rate"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="defaulting-loan-rate"
                  v-model.number="pfiLendingFormData.defaultingInterestRate"
                  :label="$t('Defaulting Loan Interest Rate')"
                  type="number"
                  :error-name="$t('Defaulting Loan Interest Rate')"
                  rules="required|max-value:100|positive"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-computation-mode"
                  v-model="pfiLendingFormData.interestAccrualMode"
                  label="Interest Computation Mode"
                  :reduce="option => option.value"
                  :options="interestComputationModeOptions"
                  placeholder="Select"
                  item="label"
                  rules="required"
                  error-name="Interest Computation Mode"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="interest-repayment"
                  v-model="pfiLendingFormData.interestRepaymentType"
                  label="Interest Repayment Option"
                  :reduce="option => option.value"
                  :options="repaymentTypeOptions"
                  placeholder="Select"
                  error-name="Interest Repayment Option"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="interest-to-be-repaid"
                  v-model.number="pfiLendingFormData.interestToBeRepaidEvery"
                  :label="$t('Interest To Be Repaid Every')"
                  type="number"
                  :error-name="$t('Interest To Be Repaid Every')"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-repayment-frequency"
                  v-model="pfiLendingFormData.interestFrequency"
                  label="Interest Repayment Frequency"
                  :reduce="option => option.value"
                  :options="repaymentFrequencyOptions"
                  placeholder="Select"
                  error-name="Interest Repayment Frequency"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="interest-installment"
                  v-model.number="pfiLendingFormData.interestInstallment"
                  :label="$t('Over')"
                  type="number"
                  :error-name="$t('Interest Installment')"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                  text="Installments"
                />
              </b-col>

              <b-col
                cols="12"
                class="mt-3 mb-2"
              >
                <h5>
                  {{ $t('Loan Moratorium') }}
                </h5>
              </b-col>

              <b-col md="12">
                <SwitchButton
                  v-model="isIgnoreProductDefault"
                  :label="$t('Ignore Product Default')"
                  name="ignore-product-default"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="alow-moratorium-loan"
                  v-model="pfiLendingFormData.allowMoratorium"
                  :label="$t('Allow Moratorium On Loan Repayment')"
                  :reduce="option => option.value"
                  :options="moratoriumTypeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Allow Moratorium On Loan Repayment')"
                  :rules="productDefaultRules"
                  item="label"
                  :readonly="!isIgnoreProductDefault"
                  :set-selected="setMoratorium"
                />
              </b-col>
            </b-row>

            <b-row v-if="isAllowMoratorium">
              <b-col
                md="6"
              >
                <SelectField
                  id="moratorium-effect"
                  v-model="pfiLendingFormData.moratoriumEffect"
                  :label="$t('Moratorium Effect On Loan Duration')"
                  :reduce="option => option.value"
                  :options="moratoriumEffectOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Moratorium Effect On Loan Duration')"
                  :rules="productDefaultRules"
                  item="label"
                  :readonly="!isIgnoreProductDefault"
                />
              </b-col>

              <b-col
                md="6"
              >
                <InputField
                  id="moratorium-duration"
                  v-model.number="pfiLendingFormData.moratoriumDuration"
                  :label="$t('Moratorium Duration')"
                  :placeholder="$t('Enter moratorium duration')"
                  type="number"
                  :error-name="$t('Moratorium Duration')"
                  :rules="moratoriumDurationRules"
                  :readonly="!isIgnoreProductDefault"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="frequency"
                  v-model="pfiLendingFormData.moratoriumFrequency"
                  :label="$t('Frequency')"
                  :options="moratoriumFrequencyOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  :error-name="$t(' Frequency')"
                  :rules="productDefaultRules"
                  item="label"
                  :readonly="!isIgnoreProductDefault"
                />
              </b-col>
            </b-row>
            <b-row v-if="showComputedInterest">
              <b-col
                md="6"
              >
                <TextAreaField
                  id="computed-interest"
                  v-model="computedInterest"
                  :label="$t('Computed Interest')"
                  type="text"
                  rows="2"
                  readonly
                />
              </b-col>
            </b-row>
            <div class="d-flex flex-wrap">
              <!-- <Button
                v-if="actionType !== 'view'"
                :label="$t('Compute Interest')"
                :loading="isLoadingComputeInterest"
                :disabled="isDisabledComputeInterest"
                size="sm"
                class="mt-3 mb-2"
                @click="computeInterest"
              /> -->

              <Button
                v-if="actionType !== 'view'"
                :label="$t('View Repayment Schedule')"
                :loading="isLoadingComputeInterest"
                :disabled="isDisabledComputeInterest"
                size="sm"
                class="mt-3 mb-2 ml-lg-2 ml-md-2"
                @click="onViewRepaymentSchedule"
              />
            </div>
          </b-form>
        </validation-observer>
      </tab-content>

      <!--Collateral & Security Deposit Informations -->
      <tab-content
        :title="$t('Collateral & Security')"
        :before-change="validateCollateralAndSecurity"
      >
        <validation-observer ref="collateralSecurity">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Collateral & Security Deposit Information') }}
                </h5>
              </b-col>

              <b-col
                md="6"
              >
                <SelectField
                  id="collateral-type"
                  v-model="pfiLendingFormData.collateralCode"
                  label="Type of Collateral"
                  placeholder="Select"
                  :options="collateralTypeOptions"
                  :reduce="option => option.value"
                  error-name="Type of Collateral"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="setCollateralInformation"
                />
              </b-col>

              <b-col md="6">
                <InputNumberField
                  id="collateral-valuation"
                  v-model="pfiCollateralData.collateralValuation"
                  label="Collateral Valuation"
                  placeholder="Enter Collateral Valuation"
                  type="text"
                  for="name"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="detailed-description"
                  v-model="pfiCollateralData.collateralDescription"
                  label="Detailed Description"
                  placeholder="Enter description"
                  type="text"
                  for="name"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="detailed-current-location"
                  v-model="pfiCollateralData.location"
                  label="Collateral Current Location"
                  placeholder="Enter current location"
                  type="text"
                  for="name"
                  readonly
                />
              </b-col>
              <b-col
                md="6"
              >
                <SelectField
                  id="collateral-currency-domiciliation"
                  v-model="pfiCollateralData.currencyCode"
                  label="Collateral's Currency Domiciliation"
                  placeholder="Select"
                  :options="currencyDomiciliationTypeOptions"
                  :reduce="option => option.value"
                  item="label"
                  readonly
                />
              </b-col>
              <b-col
                md="6"
              >
                <SelectField
                  id="perfect-status"
                  v-model="pfiCollateralData.perfectionStatus"
                  label="Collateral Perfection Status"
                  placeholder="Select"
                  :options="perfectionStatusOptions"
                  :reduce="option => option.value"
                  item="label"
                  readonly
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('End Beneficiary')"
        :before-change="validateEndBeneficiary"
      >
        <validation-observer ref="endBeneficiaryRule">
          <b-form>
            <b-row>
              <b-col md="12">
                <h5 class="section-label  mt-2">
                  {{ $t('End Beneficiary') }}
                </h5>
              </b-col>
            </b-row>
            <b-row class="match-height mb-2 mt-1">
              <b-col
                md="6"
              >
                <SwitchButton
                  v-model="pfiLendingFormData.hasEndBorrowersList"
                  :label="$t('End Beneficiary Upload Deadline Date')"
                  name="apply-end-beneficiary"
                  class="mt-2"
                  :readonly="isReadonly"
                />
                <DatePickerField
                  id="deadline-date"
                  v-model="pfiLendingFormData.deadlineForEndBorrowersList"
                  :label="$t('End Beneficiary Deadline')"
                  :config="datePickerConfig"
                  :min-date="new Date()"
                  :placeholder="$t('Select End Beneficiary Deadline')"
                  rules="required"
                  :error-name="$t('End Beneficiary Deadline')"
                  :disabled="isReadonly"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>
      <tab-content
        :title="$t('Download Loan Template')"
      >
        <b-form>
          <b-row>
            <b-col md="12">
              <h5 class="section-label  mt-2">
                {{ $t('Download Loan Templates') }}
              </h5>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              md="6"
              class="mt-2"
            >
              <InputField
                id="loan-application-letter"
                v-model="loanApplicationLetter"
                label=""
                type="text"
                readonly
              />
            </b-col>
            <b-col
              md="6"
              class=" mt-3"
            >
              <Button
                :label="$t('Download Loan Application Letter')"
                size="md"
                icon="DownloadIcon"
                show-icon
                class="mb-1"
                @click="downloadLoanDocumentTemplate(pfiCustomerFormData.partnerCode, 'GetLoanApplicationDeferralTemplate')"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col
              md="6"
              class="mt-2"
            >
              <InputField
                id="supplementFinancingAgreement"
                v-model="supplementaryFinancingAgreement"
                label=""
                type="text"
                readonly
              />
            </b-col>
            <b-col
              md="6"
              class=" mt-3"
            >
              <Button
                :label="$t('Download Supplementary Financing Agreement')"
                size="md"
                icon="DownloadIcon"
                show-icon
                class="mb-1"
                @click="downloadLoanDocumentTemplate(pfiCustomerFormData.partnerCode, 'GetSupplementialFinancingAgreementTemplate')"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col
              md="6"
              class="mt-2"
            >
              <InputField
                id="environmental-social-commitment-letter"
                v-model="environmentalAndSocialCommitmentLetter"
                label=""
                type="text"
                readonly
              />
            </b-col>
            <b-col
              md="6"
              class=" mt-3"
            >
              <Button
                :label="$t('Download Environmental & Social Commitment Letter')"
                size="md"
                icon="DownloadIcon"
                show-icon
                class="mb-1"
                @click="downloadLoanDocumentTemplate(pfiCustomerFormData.partnerCode, 'getCommitmentLetter')"
              />
            </b-col>
          </b-row>
        </b-form>
      </tab-content>
      <!--Loan Documents -->
      <tab-content
        :title="$t('Loan Documents')"
        :before-change="validateLoanDocuments"
      >
        <validation-observer ref="loanDocumentsRules">
          <b-form>
            <div class="w-full mt-1 p-10">
              <div class="d-flex mb-0 mt-1 align-items-center justify-content-between">
                <h4>Loan Documents Upload</h4>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="ml-4"
                  @click="addNewDocument()"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span>Add New Document</span>
                </b-button>
              </div>
              <b-row
                v-for="(item, index) in documentsFormData"
                :key="item.id || index"
                class="mb-2"
              >
                <b-col
                  md="6"
                >
                  <InputField
                    id="upload-image-title"
                    v-model="item.title"
                    label="Document Upload Type"
                    type="text"
                    for="name"
                    :error-name="item.title ? item.title : 'Document Upload Type'"
                    rules="required"
                    readonly
                    class="mb-2"
                  />
                  <label>Upload Documents File</label>
                  <div class="upload-image">
                    <Spinner :show="!!item.loading" />

                    <validation-provider
                      v-slot="{ errors }"
                      :name="`${ item.title ? `${item.title} File` : 'Upload Documents File'}`"
                      :rules="fileRules"
                    >
                      <b-form-group
                        :state="!errors.length"
                        :invalid-feedback="errors[0] || item.validationError"
                      >
                        <b-form-file
                          v-model="item.file"
                          :state="!errors.length && !item.validationError"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept="image/jpeg, image/bmp, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          plain
                          :disabled="isReadonly"
                          @change="onDocumentsUpload(index, $event)"
                        />
                        <div
                          v-if="!item.url"
                          class="upload-instruction"
                        >
                          <h4>{{ `${item.title ? `${item.title} File` : 'Upload File Document'}` }}</h4>
                          <p style="padding: 0 5px 0 5px;">
                            Drag & drop file or choose from your device to upload
                          </p>
                          <p class="format">
                            Format accepted (jpg, png, bmp, jpeg, docx, pdf)
                          </p>
                        </div>
                        <div v-else>
                          <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'|| item.fileExtension === 'bmp'">
                            <b-img
                              :src="item.url"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-if="item.fileExtension === 'pdf'"
                          >
                            <b-img
                              :src="pdfIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-if="item.fileExtension === 'vnd' || item.fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document'"
                          >
                            <b-img
                              :src="wordIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-else
                          >
                            <b-img
                              src=""
                            />
                          </div>
                          <div
                            v-if="item.url && actionType !== 'view'"
                            class="trash"
                          >
                            <feather-icon
                              icon="Trash2Icon"
                              size="20"
                              class="text-primary"
                              @click="removeDocument(item.id, index)"
                            />
                          </div>
                          <div
                            v-if="item.url"
                            class="view-details"
                          >
                            <feather-icon
                              icon="EyeIcon"
                              size="20"
                              class="text-primary"
                              @click="viewDocumentFile(item.url, item.title, item.fileExtension)"
                            />
                          </div>
                        </div>
                      </b-form-group>
                    </validation-provider>
                  </div>
                </b-col>
                <b-col
                  md="3"
                  class="mt-0"
                >
                  <b-row>
                    <!-- Remove Button -->
                    <b-col
                      v-if="item.url && actionType !== 'view'"
                      md="12"
                      class="mt-1"
                    >
                      <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="ml-0"
                        @click="removeDocument(item.id, index)"
                      >
                        <feather-icon
                          icon="XIcon"
                          class="mr-25"
                        />
                        <span>Remove</span>
                      </b-button>
                    </b-col>
                    <b-col
                      v-if="item.url"
                      md="12"
                      class="mt-1"
                    >
                      <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="ml-0"
                        @click="handleDownload(item.url)"
                      >
                        <feather-icon
                          icon="DownloadIcon"
                          class="mr-25"
                        />
                        <span>Download</span>
                      </b-button>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </div>
          </b-form>
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('Preview')"
      >
        <b-row>
          <b-col md="12">
            <h5 class="section-label  mt-2">
              {{ $t('Preview All Details') }}
            </h5>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="mb-2 mt-2"
          >
            <h5 class="mb-0">
              {{ $t('PFI Information') }}
            </h5>
          </b-col>
          <b-col md="6">
            <SelectField
              id="pfi-code"
              v-model="pfiCustomerFormData.partnerCode"
              :label="$t('PFI Code')"
              :reduce="option => option.value"
              :options="pfiPartnerOptions"
              :placeholder="$t('Select')"
              :error-name="$t('PFI Code')"
              rules="required"
              item="label"
              readonly
              :set-selected="getLinkedPfiByPartnerCode"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="pfi-name"
              v-model="pfiCustomerFormData.partnerName"
              :label="$t('PFI Name')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="pfi-state"
              v-model="pfiCustomerFormData.state"
              :label="$t('PFI State')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="pfi-contact-email-address"
              v-model="pfiCustomerFormData.contactEmailAddress"
              :label="$t('PFI Contact Email Address')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="pfi-phone-number"
              v-model="pfiCustomerFormData.contactPhoneNumber"
              :label="$t('PFI Phone Number')"
              type="text"
              readonly
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="mb-2"
          >
            <h5 class="mb-0 mt-3">
              {{ $t('Loan Disbursement Details') }}
            </h5>
          </b-col>
          <b-col md="6">
            <SelectField
              id="bank-code"
              v-model="pfiLendingFormData.externalBankName"
              label="Bank Account Name"
              :options="bankListOptions"
              placeholder="Select"
              :reduce="option => option.label"
              item="label"
              rules="required"
              error-name="Bank Account Name"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="bank-account-number"
              v-model="pfiLendingFormData.externalDisbursementAccount"
              label="Bank Account Number"
              type="number"
              rules="required|min: 10|max: 10"
              error-name="Bank Account Number"
              placeholder="Enter bank account number"
              readonly
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="mb-2"
          >
            <h5 class="mb-0">
              {{ $t('Loan Account Information') }}
            </h5>
          </b-col>

          <b-col md="6">
            <InputField
              id="loan-pfi-name"
              v-model="pfiCustomerFormData.partnerName"
              label="PFI Name"
              type="text"
              error-name="PFI Name"
              rules="required"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="purpose-loan-application"
              v-model="pfiLendingFormData.description"
              label="Purpose of Loan Application"
              type="text"
              error-name="Purpose of Loan Application"
              rules="required"
              readonly
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="loan-product"
              v-model="pfiLendingFormData.externalProductCode"
              label="Loan Product"
              :reduce="option => option.value"
              :options="loanProductOptions"
              placeholder="Select"
              error-name="Loan Product"
              rules="required"
              item="label"
              readonly
              :set-selected="setLoanFee"
            />
          </b-col>
          <b-col md="6">
            <InputNumberField
              id="loan-principal-amount"
              v-model="pfiLendingFormData.amount"
              label="Loan Principal Amount"
              type="text"
              error-name="Loan Principal Amount"
              rules="required"
              readonly
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="mt-3 mb-2"
          >
            <h5>
              {{ $t('Principal Repayment Information') }}
            </h5>
          </b-col>
          <b-col md="6">
            <SelectField
              id="principal-repayment"
              v-model="pfiLendingFormData.principalRepaymentType"
              label="Principal Repayment Option"
              :reduce="option => option.value"
              :options="repaymentTypeOptions"
              placeholder="Select"
              error-name="Principal Repayment Option"
              rules="required"
              item="label"
              readonly
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <InputField
              id="principal-to-be-repaid"
              v-model.number="pfiLendingFormData.principalToBeRepaidEvery"
              :label="$t('Principal To Be Repaid Every')"
              type="number"
              :error-name="$t('Principal To Be Repaid Every')"
              rules="required|integer|positive"
              readonly
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="principal-repayment-frequency"
              v-model="pfiLendingFormData.principalFrequency"
              label="Principal Repayment Frequency"
              :reduce="option => option.value"
              :options="repaymentFrequencyOptions"
              placeholder="Select"
              error-name="Principal Repayment Frequency"
              rules="required"
              item="label"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="principal-installment"
              v-model.number="pfiLendingFormData.principalInstallment"
              :label="$t('Over')"
              type="number"
              :error-name="$t('Principal Installment')"
              rules="required|integer|positive"
              readonly
              text="Installments"
            />
          </b-col>
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="pfiLendingFormData.effectiveDate"
              rules="required"
              :label="$t('Loan Start Date')"
              :error-name="$t('Loan Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Select Loan Start Date')"
              disabled
              @input="setEndDate"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="tenure"
              v-model.number="pfiLendingFormData.tenure"
              label="Tenure (In Days)"
              type="number"
              error-name="Tenure"
              rules="required"
              readonly
              @input="setEndDate"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="maturity-date"
              v-model="pfiLendingFormData.maturityDate"
              label="Maturity Date"
              type="text"
              rules="required"
              error-name="Maturity Date"
              readonly
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            class="mt-3 mb-2"
          >
            <h5>
              {{ $t('Interest Repayment Information') }}
            </h5>
          </b-col>
          <b-col md="6">
            <InputField
              id="applicable-interest-rate"
              v-model.number="pfiLendingFormData.interestRate"
              label="Applicable Interest Rate"
              type="number"
              rules="required"
              error-name="Applicable Interest Rate"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="defaulting-loan-rate"
              v-model.number="pfiLendingFormData.defaultingInterestRate"
              :label="$t('Defaulting Loan Interest Rate')"
              type="number"
              :error-name="$t('Defaulting Loan Interest Rate')"
              rules="required|max-value:100|positive"
              readonly
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="interest-computation-mode"
              v-model="pfiLendingFormData.interestAccrualMode"
              label="Interest Computation Mode"
              :reduce="option => option.value"
              :options="interestComputationModeOptions"
              placeholder="Select"
              item="label"
              rules="required"
              error-name="Interest Computation Mode"
              readonly
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="interest-repayment"
              v-model="pfiLendingFormData.interestRepaymentType"
              label="Interest Repayment Option"
              :reduce="option => option.value"
              :options="repaymentTypeOptions"
              placeholder="Select"
              error-name="Interest Repayment Option"
              rules="required"
              item="label"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="interest-to-be-repaid"
              v-model.number="pfiLendingFormData.interestToBeRepaidEvery"
              :label="$t('Interest To Be Repaid Every')"
              type="number"
              :error-name="$t('Interest To Be Repaid Every')"
              rules="required|integer|positive"
              readonly
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="interest-repayment-frequency"
              v-model="pfiLendingFormData.interestFrequency"
              label="Interest Repayment Frequency"
              :reduce="option => option.value"
              :options="repaymentFrequencyOptions"
              placeholder="Select"
              error-name="Interest Repayment Frequency"
              rules="required"
              item="label"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="interest-installment"
              v-model.number="pfiLendingFormData.interestInstallment"
              :label="$t('Over')"
              type="number"
              :error-name="$t('Interest Installment')"
              rules="required|integer|positive"
              readonly
              text="Installments"
            />
          </b-col>

          <b-col
            cols="12"
            class="mt-3 mb-2"
          >
            <h5>
              {{ $t('Loan Moratorium') }}
            </h5>
          </b-col>

          <b-col md="12">
            <SwitchButton
              v-model="isIgnoreProductDefault"
              :label="$t('Ignore Product Default')"
              name="ignore-product-default"
              class="mt-2"
              readonly
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="alow-moratorium-loan"
              v-model="pfiLendingFormData.allowMoratorium"
              :label="$t('Allow Moratorium On Loan Repayment')"
              :reduce="option => option.value"
              :options="moratoriumTypeOptions"
              :placeholder="$t('Select')"
              :error-name="$t('Allow Moratorium On Loan Repayment')"
              :rules="productDefaultRules"
              item="label"
              :readonly="!isIgnoreProductDefault"
              :set-selected="setMoratorium"
            />
          </b-col>
        </b-row>

        <b-row v-if="isAllowMoratorium">
          <b-col
            md="6"
          >
            <SelectField
              id="moratorium-effect"
              v-model="pfiLendingFormData.moratoriumEffect"
              :label="$t('Moratorium Effect On Loan Duration')"
              :reduce="option => option.value"
              :options="moratoriumEffectOptions"
              :placeholder="$t('Select')"
              :error-name="$t('Moratorium Effect On Loan Duration')"
              :rules="productDefaultRules"
              item="label"
              :readonly="!isIgnoreProductDefault"
            />
          </b-col>

          <b-col
            md="6"
          >
            <InputField
              id="moratorium-duration"
              v-model.number="pfiLendingFormData.moratoriumDuration"
              :label="$t('Moratorium Duration')"
              :placeholder="$t('Enter moratorium duration')"
              type="number"
              :error-name="$t('Moratorium Duration')"
              :rules="moratoriumDurationRules"
              :readonly="!isIgnoreProductDefault"
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="frequency"
              v-model="pfiLendingFormData.moratoriumFrequency"
              :label="$t('Frequency')"
              :options="moratoriumFrequencyOptions"
              :reduce="option => option.value"
              :placeholder="$t('Select')"
              :error-name="$t(' Frequency')"
              :rules="productDefaultRules"
              item="label"
              :readonly="!isIgnoreProductDefault"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="mb-2"
          >
            <h5 class="mb-0 mt-3">
              {{ $t('Collateral & Security Deposit Information') }}
            </h5>
          </b-col>

          <b-col
            md="6"
          >
            <SelectField
              id="collateral-type"
              v-model="pfiLendingFormData.collateralCode"
              label="Type of Collateral"
              placeholder="Select"
              :options="collateralTypeOptions"
              :reduce="option => option.value"
              error-name="Type of Collateral"
              rules="required"
              item="label"
              readonly
              :set-selected="setCollateralInformation"
            />
          </b-col>

          <b-col md="6">
            <InputNumberField
              id="collateral-valuation"
              v-model="pfiCollateralData.collateralValuation"
              label="Collateral Valuation"
              placeholder="Enter Collateral Valuation"
              type="text"
              for="name"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="detailed-description"
              v-model="pfiCollateralData.collateralDescription"
              label="Detailed Description"
              placeholder="Enter description"
              type="text"
              for="name"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="detailed-current-location"
              v-model="pfiCollateralData.location"
              label="Collateral Current Location"
              placeholder="Enter current location"
              type="text"
              for="name"
              readonly
            />
          </b-col>
          <b-col
            md="6"
          >
            <SelectField
              id="collateral-currency-domiciliation"
              v-model="pfiCollateralData.currencyCode"
              label="Collateral's Currency Domiciliation"
              placeholder="Select"
              :options="currencyDomiciliationTypeOptions"
              :reduce="option => option.value"
              item="label"
              readonly
            />
          </b-col>
          <b-col
            md="6"
          >
            <SelectField
              id="perfection-status"
              v-model="pfiCollateralData.perfectionStatus"
              label="Collateral Perfection Status"
              placeholder="Select"
              :options="perfectionStatusOptions"
              :reduce="option => option.value"
              item="label"
              readonly
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <h5 class="mb-0 mt-3">
              {{ $t('End Beneficiary') }}
            </h5>
          </b-col>
        </b-row>
        <b-row class="match-height mb-2 mt-1">
          <b-col
            md="6"
          >
            <SwitchButton
              v-model="pfiLendingFormData.hasEndBorrowersList"
              :label="$t('End Beneficiary Upload Deadline Date')"
              name="apply-end-beneficiary"
              class="mt-2"
              readonly
            />
            <DatePickerField
              id="deadline-date"
              v-model="pfiLendingFormData.deadlineForEndBorrowersList"
              :label="$t('End Beneficiary Deadline')"
              :config="datePickerConfig"
              :min-date="new Date()"
              :placeholder="$t('Select End Beneficiary Deadline')"
              rules="required"
              :error-name="$t('End Beneficiary Deadline')"
              disabled
            />
          </b-col>
        </b-row>
        <form>
          <div class="w-full p-10">
            <div class="d-flex mb-0 mt-1 align-items-center justify-content-between">
              <h5 class="mb-2 mt-3">
                {{ $t('Loan Documents Upload') }}
              </h5>
            </div>
            <b-row
              v-for="(item, index) in documentsFormData"
              :key="item.id || index"
              class="mb-2"
            >
              <b-col
                md="6"
              >
                <InputField
                  id="upload-image-title"
                  v-model="item.title"
                  label="Document Upload Type"
                  type="text"
                  for="name"
                  :error-name="item.title ? item.title : 'Document Upload Type'"
                  rules="required"
                  readonly
                  class="mb-2"
                />
                <label>Upload Documents File</label>
                <div class="upload-image">
                  <Spinner :show="!!item.loading" />

                  <validation-provider
                    v-slot="{ errors }"
                    :name="`${ item.title ? `${item.title} File` : 'Upload Documents File'}`"
                    :rules="fileRules"
                  >
                    <b-form-group
                      :state="!errors.length"
                      :invalid-feedback="errors[0] || item.validationError"
                    >
                      <b-form-file
                        :state="!errors.length && !item.validationError"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept="image/jpeg, image/bmp, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        plain
                        disabled
                        @change="onDocumentsUpload(index, $event)"
                      />
                      <div
                        v-if="!item.url"
                        class="upload-instruction"
                      >
                        <h4>{{ `${item.title ? `${item.title} File` : 'Upload File Document'}` }}</h4>
                        <p style="padding: 0 5px 0 5px;">
                          Drag & drop file or choose from your device to upload
                        </p>
                        <p class="format">
                          Format accepted (jpg, png, bmp, jpeg, docx, pdf)
                        </p>
                      </div>
                      <div v-else>
                        <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'|| item.fileExtension === 'bmp'">
                          <b-img
                            :src="item.url"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-if="item.fileExtension === 'pdf'"
                        >
                          <b-img
                            :src="pdfIcon"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-if="item.fileExtension === 'vnd' || item.fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document'"
                        >
                          <b-img
                            :src="wordIcon"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-else
                        >
                          <b-img
                            src=""
                          />
                        </div>
                        <div
                          v-if="item.url"
                          class="view-details"
                        >
                          <feather-icon
                            icon="EyeIcon"
                            size="20"
                            class="text-primary"
                            @click="viewDocumentFile(item.url, item.title, item.fileExtension)"
                          />
                        </div>
                      </div>
                    </b-form-group>
                  </validation-provider>
                </div>
              </b-col>
            </b-row>
          </div>
        </form>
      </tab-content>

      <tab-content
        :title="$t('Terms And Conditions')"
        :before-change="validateTermsAndConditions"
      >
        <ValidationObserver ref="termsOfUseRules">
          <b-form>
            <b-row>
              <b-col md="12">
                <validation-observer ref="termsOfUseRules">
                  <b-form>
                    <b-row>
                      <b-col md="12">
                        <div>
                          <b-row>
                            <b-col md="9">
                              <h4 class="mb-2">
                                <strong><u>Terms And Conditions</u></strong>
                              </h4>
                            </b-col>
                            <b-col md="3">
                              <Button
                                :label="$t('Download T&C')"
                                size="md"
                                icon="DownloadIcon"
                                show-icon
                                class="mb-2"
                              />
                            </b-col>
                          </b-row>
                          <p
                            class="mb-2"
                            style="text-align: justify;"
                          >
                            Please read these Terms and Conditions ("Terms") carefully before proceeding with any loan booking through our platform.
                            These Terms constitute a binding agreement between the borrower ("you", "your", "borrower") and [Your Company Name] ("we", "us", "our", "the Lender").
                            By booking a loan or proceeding with any part of the loan application process, you agree to be bound by these Terms, as well as our Privacy Policy,
                            Disclosures, and any other applicable documentation or notices presented to you.
                          </p>

                          <div class="terms-and-conditions">
                            <h5><strong>1. Loan Eligibility</strong></h5>
                            <ul>
                              <li>You must be at least 18 years old (or the age of majority in your jurisdiction).</li>
                              <li>You must be a citizen or legal resident of [Country].</li>
                              <li>You must provide accurate, complete, and verifiable information.</li>
                              <li>Your creditworthiness must meet our lending criteria.</li>
                              <li>The loan amount, term, and interest rate will be subject to approval.</li>
                              <li>Providing false or misleading information may result in legal action and disqualification from the loan process.</li>
                            </ul>

                            <h5><strong>2. Loan Application Process</strong></h5>
                            <p>You may initiate a loan application through our online or offline channels. The application process may involve:</p>
                            <ul>
                              <li>Submission of KYC documents.</li>
                              <li>Credit assessment, including access to your credit score.</li>
                              <li>Verification of income and employment.</li>
                              <li>Other due diligence as may be deemed necessary.</li>
                            </ul>
                            <p>We reserve the right to approve or reject any application at our sole discretion, with or without providing a reason.</p>

                            <h5><strong>3. Loan Terms</strong></h5>
                            <p>Upon approval, you will be issued a Loan Sanction Letter outlining the:</p>
                            <ul>
                              <li>Loan amount</li>
                              <li>Interest rate (fixed or variable)</li>
                              <li>Repayment schedule</li>
                              <li>Processing fees</li>
                              <li>Late payment charges</li>
                              <li>Prepayment or foreclosure clauses</li>
                            </ul>
                            <p>You are required to review and digitally or physically sign the Loan Agreement before disbursal. The agreement becomes legally binding once both parties have signed.</p>

                            <h5><strong>4. Disbursal of Loan</strong></h5>
                            <p>The approved loan amount will be disbursed to your designated bank account after:</p>
                            <ul>
                              <li>Loan agreement is executed</li>
                              <li>All required documents are submitted and verified</li>
                              <li>Any applicable processing fees are paid</li>
                            </ul>
                            <p>We may deduct applicable charges such as processing fees, insurance premiums, or taxes from the disbursed amount.</p>

                            <h5><strong>5. Repayment Terms</strong></h5>
                            <ul>
                              <li>Repayment must be made in accordance with the repayment schedule agreed upon.</li>
                              <li>Monthly EMI (Equated Monthly Installments) debited through NACH, ECS, or any authorized auto-debit facility.</li>
                              <li>Interest is calculated on a reducing balance basis unless otherwise specified.</li>
                              <li>Failure to maintain sufficient funds for EMIs may lead to penalties, legal action, and damage to your credit history.</li>
                            </ul>

                            <h5><strong>6. Late Payment & Default</strong></h5>
                            <p>Late EMI payments will attract penalties as specified in the loan agreement. Continuous default or non-payment may result in:</p>
                            <ul>
                              <li>Reporting to credit bureaus</li>
                              <li>Legal recovery actions</li>
                              <li>Engagement of recovery agents</li>
                              <li>Seizure of pledged collateral (if applicable)</li>
                            </ul>
                            <p>You are responsible for informing us of any change in bank details or financial hardship that may affect repayments.</p>

                            <h5><strong>7. Prepayment and Foreclosure</strong></h5>
                            <p>You may choose to prepay or foreclose your loan, subject to the terms mentioned in your agreement. Prepayment may involve:</p>
                            <ul>
                              <li>No charges for floating rate loans as per RBI guidelines</li>
                              <li>Applicable charges for fixed rate loans or business loans</li>
                              <li>Written notice and clearance of outstanding dues</li>
                            </ul>
                            <p>We encourage borrowers to request a foreclosure statement for clarity.</p>

                            <h5><strong>8. Charges and Fees</strong></h5>
                            <p>Applicable charges may include:</p>
                            <ul>
                              <li>Processing fee</li>
                              <li>Loan cancellation fee (if applicable)</li>
                              <li>EMI bounce charges</li>
                              <li>Loan restructuring fees</li>
                              <li>Document retrieval or statement charges</li>
                            </ul>
                            <p>A complete fee schedule is available in your sanction letter or on our website.</p>

                            <h5><strong>9. Use of Funds</strong></h5>
                            <p>The loan must be used strictly for the declared purpose, as outlined during the booking process. Misuse of funds or diversion for illegal activities will result in legal action, loan cancellation, or immediate recall of the entire loan amount.</p>

                            <h5><strong>10. Right to Amend</strong></h5>
                            <ul>
                              <li>We reserve the right to amend these Terms, interest rates, repayment terms, or any service features, provided:</li>
                              <li>You are notified in advance through email, SMS, or official correspondence.</li>
                              <li>Any adverse change will only apply prospectively.</li>
                              <li>Continued use of our services after such amendments shall constitute your acceptance.</li>
                            </ul>

                            <h5><strong>11. Communications and Notices</strong></h5>
                            <p>You agree to receive communications related to your loan via:</p>
                            <ul>
                              <li>SMS</li>
                              <li>Email</li>
                              <li>Phone calls</li>
                              <li>WhatsApp (if opted in)</li>
                              <li>Postal mail</li>
                            </ul>
                            <p>You are responsible for keeping your contact information up to date. We will not be liable for non-receipt of critical information due to outdated details.</p>

                            <h5><strong>12. Confidentiality and Data Protection</strong></h5>
                            <p>We collect and process your personal data in accordance with our Privacy Policy and relevant data protection laws. This may include sharing data with:</p>
                            <ul>
                              <li>Credit bureaus</li>
                              <li>Regulatory authorities</li>
                              <li>Service providers (e.g., payment processors, verification agencies)</li>
                              <li>Insurance partners (if insurance is bundled)</li>
                            </ul>
                            <p>We ensure your data is secured with appropriate technical and organizational measures.</p>

                            <h5><strong>13. Termination</strong></h5>
                            <p>We may terminate or cancel your loan agreement:</p>
                            <ul>
                              <li>Upon full repayment of the loan</li>
                              <li>If you commit a material breach of these Terms</li>
                              <li>If we discover fraud or misrepresentation</li>
                              <li>In case of legal prohibition or regulatory direction</li>
                            </ul>
                            <p>You may also cancel your loan before disbursal, subject to cancellation fees.</p>

                            <h5><strong>14. Dispute Resolution</strong></h5>
                            <ul>
                              <li>All disputes arising from this agreement will be:</li>
                              <li>First resolved through amicable discussion</li>
                              <li>Then referred to mediation or arbitration (if mutually agreed)</li>
                              <li>Subject to jurisdiction of courts in [City], [Country]</li>
                            </ul>
                            <p>You agree to waive rights to class-action lawsuits or jury trials where permissible.</p>

                            <h5><strong>15. Limitation of Liability</strong></h5>
                            <p>To the fullest extent permitted by law, we are not liable for:</p>
                            <ul>
                              <li>Any indirect, incidental, or consequential damages</li>
                              <li>Loss arising from delay or failure due to force majeure</li>
                              <li>Errors or failures in third-party services or payment gateways</li>
                            </ul>
                            <p>Our liability, if any, will be limited to the principal amount of the loan.</p>

                            <h5><strong>16. Governing Law</strong></h5>
                            <p>This agreement shall be governed by and interpreted in accordance with the laws of [Country/State], without regard to its conflict of law principles.</p>

                            <h5><strong>17. Acceptance</strong></h5>
                            <p>By applying for or booking a loan with us, you acknowledge that:</p>
                            <ul>
                              <li>You have read and understood these Terms</li>
                              <li>You agree to be legally bound by them</li>
                              <li>You have the capacity to enter into a binding financial agreement</li>
                            </ul>
                            <p>If you do not agree to any of these Terms, you must not proceed with the loan booking.</p>
                          </div>

                        </div>
                      </b-col>
                      <b-col md="12">
                        <ValidationProvider
                          name="Terms and Conditions"
                          rules="required"
                        >
                          <b-form-group
                            class="pt-2"
                            label=""
                            :state="!customErrors.agreeToTerms"
                            :invalid-feedback="customErrors.agreeToTerms"
                          >
                            <b-form-checkbox
                              v-model="pfiLendingFormData.hasCheckedAcceptanceForm"
                              name="check-button"
                              inline
                              :disabled="isReadonly"
                              @input="setTermsAndConditions"
                            >
                              I have Read, Understood and Accept the Terms and Conditions.
                            </b-form-checkbox>
                            <b-form-invalid-feedback>
                              {{ customErrors.agreeToTerms }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>
                  </b-form>
                </validation-observer>
              </b-col>
            </b-row>
          </b-form>
        </validationobserver>
      </tab-content>

      <!-- Customer Tab -->
      <template #footer>
        <div class="wizard-footer-left">
          <button
            v-if="activateTabIndex > 0"
            class="wizard-btn"
            @click="prevTab()"
          >
            {{ $t('Previous') }}
          </button>
        </div>

        <div class="wizard-footer-right">
          <button
            v-if="activateTabIndex === 5"
            class="wizard-btn save-exit-btn"
            :loading="isLoading"
            :disabled="isDisabled"
            @click="handleSaveProgress"
          >
            {{ $t('Save and Exit') }}
          </button>

          <button
            v-if="activateTabIndex < 8"
            class="wizard-btn"
            @click="nextTab()"
          >
            {{ $t('Next') }}
          </button>
          <button
            v-if="activateTabIndex >= 8 && actionType !== 'view'"
            class="wizard-btn finish-btn"
            :label="$t('Submit')"
            :loading="isSubmitLoading"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
          >
            {{ $t('Submit') }}
          </button>
        </div>
      </template>
      <FullScreenSpinner :show="showSpinner" />
    </form-wizard>

    <b-modal
      id="repayment-schedule-modal"
      ref="repaymentScheduleModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Repayment Schedule')"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <TableEntries
              v-model="repaymentSchedulePagination.perPage"
              :options="perPageOptions"
            />

          </b-col>

          <!-- Export -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-dropdown
                id="dropdown-1"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :text="$t('Export')"
                variant="primary"
              >
                <b-dropdown-item @click="csvExport(csvData)">
                  {{ $t('CSV') }}
                </b-dropdown-item>
                <b-dropdown-item @click="excelExport(csvData)">
                  {{ $t('Excel') }}
                </b-dropdown-item>
                <b-dropdown-item @click="pdfExport()">
                  {{ $t('PDF') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </b-col>

        </b-row>

      </div>

      <b-table
        ref="repaymentScheduleModalTable"
        select-mode="single"
        :items="repaymentSchedules"
        responsive
        no-local-sorting
        :fields="translatedRepaymentScheduleTableHeaders"
        :per-page="repaymentSchedulePagination.perPage"
        :current-page="repaymentSchedulePagination.currentPage"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        class="position-relative mt-2"
      >
        <template #cell(SN)="data">
          {{ getPageNumber(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="repaymentSchedulePagination.currentPage"
        :from="repaymentFrom"
        :to="repaymentTo"
        :total-rows="repaymentSchedulePagination.totalEntries"
        :per-page="repaymentSchedulePagination.perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>

    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >

      <div class="m-2 text-center">

        <b-row>
          <b-col cols="12">

            <b-avatar
              size="30rem"
              :src="avatarLg"
            />
          </b-col>
        </b-row>
      </div>

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @cancel="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <div v-if="fileExtension === 'png'|| fileExtension === 'jpeg'|| fileExtension === 'jpg'">
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'pdf'">
              <b-img
                :src="pdfIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'vnd' || fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document'">
              <b-img
                :src="wordIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else>
              <b-img
                src=""
              />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <!-- Display Validation Errors -->
    <b-modal
      id="all-validated-modal"
      ref="allValidatedModal"
      ok-title="Cancel"
      ok-only
      no-close-on-backdrop
      ok-variant="outline-secondary"
      centered
      size="md"
      title="Kindly attend to the field(s) below"
      @hidden="resetValidationModal"
    >
      <div
        v-for="(errorItem, index) in allValidationErrors"
        :key="index"
      >
        <p>{{ `${index + 1}.` }} <span style="color: red;"> {{ errorItem }}</span></p>
      </div>
    </b-modal>
    <!-- Display Validation Errors -->
    <FullScreenSpinner :show="showSpinner" />
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { heightFade } from '@core/directives/animations'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { toastAlert } from '@core/mixins/toast'
import { maxValue, positive, required } from '@validations'
import {
  BAvatar, BCol, BDropdown, BDropdownItem, BForm, BRow, BTable,
  VBTooltip, BFormGroup, BFormFile, BImg, BButton, BFormCheckbox,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import Ripple from 'vue-ripple-directive'
import { mapActions, mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import * as FileSaver from 'file-saver'
import { lmsExportToCSV, lmsExportToExcel, lmsExportToPDF } from '@/helpers/export'
import {
  toBalance, toKobo, addNumberSeparator, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import Spinner from '@/views/components/spinner/Spinner.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import addSpaceToString from '../../../../../helpers/addSpaceToString'
import {
  moratoriumEffect, moratoriumType, repaidFrequencies, pdfIcon, wordIcon,
} from '../../../../../helpers/data'
import {
  toDateWithMonth, toDateWithYear, toYearWithDate, checkDateFormat,
} from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import Button from '../../../../components/button/Button.vue'
import SwitchButton from '../../../../components/switch/Switch.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { lmsPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    FormWizard,
    TabContent,
    BRow,
    BForm,
    BCol,
    Button,
    BTable,
    BFormInvalidFeedback,
    Spinner,
    FullScreenSpinner,
    SwitchButton,
    DatePickerField,
    InputField,
    SelectField,
    ValidationObserver,
    ValidationProvider,
    BAvatar,
    TablePagination,
    InputNumberField,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormCheckbox,
    BFormFile,
    BImg,
    BButton,
    TableEntries,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert],
  props: {
    initialLogoSrc: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fileRules: 'required',
      toDateWithMonth,
      toDateWithYear,
      toYearWithDate,
      checkDateFormat,
      formatAmount,
      toBalance,
      required,
      maxValue,
      positive,
      isDisabled: false,
      isSubmitDisabled: true,
      isSubmitLoading: false,
      isIgnoreProductDefault: false,
      isLoading: false,
      isEditable: false,
      isAllowMoratorium: false,
      isRepaymentType: false,
      isInterestRepaymentType: false,
      isReadonly: false,
      showSpinner: false,
      showComputedInterest: false,
      showBackButton: false,
      showCustomerInfo: false,
      showFormErrorAlert: false,

      sortBy: 'partnerCode',
      productDefaultRules: '',
      rules: '',
      moratoriumDurationRules: '',
      searchType: '',

      pfiCustomerFormData: {
        partnerName: '',
        partnerCode: '',
        state: '',
        contactEmailAddress: '',
        contactPhoneNumber: '',
      },
      pfiLendingFormData: {
        externalBankName: '',
        externalDisbursementAccount: '',
        partnerName: '',
        description: '',
        externalProductCode: '',
        amount: '',
        tenure: '',
        principalRepaymentType: '',
        principalFrequency: '',
        interestInstallment: '',
        interestRepaymentType: '',
        interestFrequency: '',
        interestAccrualMode: '',
        collateralCode: '',
        // documentIdentifier: '',
        effectiveDate: '',
        maturityDate: '',
        principalToBeRepaidEvery: '',
        principalInstallment: '',
        defaultingInterestRate: '',
        interestRate: '',
        interestToBeRepaidEvery: '',
        allowMoratorium: '',
        moratoriumEffect: '',
        moratoriumDuration: '',
        moratoriumFrequency: '',
        hasCheckedAcceptanceForm: false,
        hasEndBorrowersList: true,
        deadlineForEndBorrowersList: null,
        id: null,
      },
      loanProductOptions: [],
      repaymentTypeOptions: [
        { label: 'End of Contract', value: 'EndOfContract' },
        { label: 'Pro Rated', value: 'Prorated' },
      ],
      repaymentFrequencyOptions: [
        { label: 'Months', value: 'Monthly' },
        { label: 'Quarterly', value: 'Quarterly' },
        { label: 'Half-Yearly', value: 'HalfYearly' },
        { label: 'Years', value: 'Annually' },

      ],
      interestComputationModeOptions: [
        { label: 'Fixed', value: 'Fixed' },
        { label: 'Reducing Balance', value: 'ReducingBalance' },
        { label: 'Equal Monthly Instalment', value: 'EqualMonthlyInstalment' },
      ],

      pfiCollateralData: {
        collateralValuation: 0,
        collateralDescription: '',
        location: '',
        currencyCode: '',
        perfectionStatus: '',
      },

      isLoadingComputeInterest: false,
      isDisabledComputeInterest: false,
      isLoadingRepaymentSchedule: false,
      isDisabledRepaymentSchedule: false,
      actionType: 'add',

      computedInterest: null,

      repaymentSchedules: [],
      newRepaymentSchedules: [],
      repaymentSchedulePagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },

      repaymentScheduleTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'paymentDate', sortable: true, label: 'Repayment Date' },
        { key: 'loanAccountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'principal', sortable: true, label: 'Principal' },
        { key: 'interest', sortable: true, label: 'Interest' },
        { key: 'fee', sortable: true, label: 'Loan Fee' },
        { key: 'totalRepayment', sortable: true, label: 'Total Repayment' },
        // { key: 'paymentStatus', sortable: true, label: 'Payment Status' },
      ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        minDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'd/m/Y',
      },
      permission: '',
      getStatusColor,
      avatarLg: '',
      avatarModalTitle: '',
      moratoriumEffectOptions: moratoriumEffect,
      moratoriumFrequencyOptions: repaidFrequencies,
      moratoriumTypeOptions: moratoriumType,
      annualPrincipalFrequency: '',
      customer: {
        customerName: '',
        branchName: '',
        accountName: '',
        productName: '',
        accountOfficerName: '',
        accountStatus: '',
        hasOutstandingLoan: false,
        outstandingLoan: null,
      },
      pdfIcon,
      wordIcon,
      remitaLookUpData: {
        firstName: '',
        lastName: '',
        middleName: '',
        accountNumber: '',
        bvn: '',
        bankCode: '',
        authorisationCode: '',
      },
      loanHistoryDetails: [],
      loanHistoryDetailsPagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      bankListOptions: [],
      collectionProviderCustomerID: '',
      collectionAuthorisationCode: '',
      equalMonthlyInstallment: false,
      getInterestInstallment: '',
      previousLoanAccountData: {
        principalToBeRepaidEvery: '',
        principalFrequency: '',
        interestAccrualMode: '',
        interestRepaymentType: '',
        interestToBeRepaidEvery: '',
        interestFrequency: '',
        interestInstallment: '',
      },

      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      documentLoadSpinner: false,
      documentIdentifier: '',
      documentsFormData: [{
        type: '',
        url: null,
        file: [],
        loading: false,
        validationError: '',
        title: 'Loan Application Letter',
      },
      {
        type: '',
        url: null,
        file: [],
        loading: false,
        validationError: '',
        title: 'Environmental & Social Commitment Letter',
      },
      {
        type: '',
        url: null,
        file: [],
        loading: false,
        validationError: '',
        title: 'Supplementary Financing Agreement',
      },
      ],
      loanApplicationLetter: 'Loan Application Letter',
      supplementaryFinancingAgreement: 'Supplementary Financing Agreement',
      environmentalAndSocialCommitmentLetter: 'Environmental & Social Commitment Letter',
      collateralTypeOptions: [],
      currencyDomiciliationTypeOptions: [
        { value: 'NGN', label: 'NGN' },
        { value: 'Dollars', label: 'Dollars' },
      ],
      perfectionStatusOptions: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ],
      pfiPartnerOptions: [],
      activateTabIndex: 0,
      actionCategory: '',
      isEndBeneficiaryRule: false,
      customErrors: {
        agreeToTerms: '',
      },
      allValidationErrors: [],
    }
  },
  computed: {
    searchButtonClass() {
      if (this.actionType === 'add') {
        return ''
      }
      return 'd-none'
    },
    repaymentFrom() {
      return (this.repaymentSchedulePagination.currentPage * this.repaymentSchedulePagination.perPage) - (this.repaymentSchedulePagination.perPage - 1)
    },
    repaymentTo() {
      return Math.min(this.repaymentFrom + this.repaymentSchedulePagination.perPage - 1, this.repaymentSchedulePagination.totalEntries)
    },
    translatedRepaymentScheduleTableHeaders() {
      return this.repaymentScheduleTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    csvData() {
      return this.newRepaymentSchedules.map(item => ({
        'Repayment Date': item.paymentDate,
        'Loan Account Number': item.loanAccountNumber,
        Principal: addNumberSeparator(item.principal),
        Interest: addNumberSeparator(item.interest),
        'Loan Fee': addNumberSeparator(item.fee),
        'Total Repayment': addNumberSeparator(item.totalRepayment),
        // 'Payment Status': item.paymentStatus,
      }))
    },
    ...mapGetters({
      corporateInformation: 'operations/getCorporateDetails',
      userDetails: 'auth/getCurrentUser',
    }),
  },
  watch: {
    isIgnoreProductDefault(newValue) {
      if (newValue === true) {
        this.productDefaultRules = 'required'
        this.moratoriumDurationRules = 'required|integer|positive'
      } else {
        this.productDefaultRules = ''
        this.moratoriumDurationRules = ''
        this.pfiLendingFormData.allowMoratorium = ''
        this.pfiLendingFormData.moratoriumEffect = ''
        this.pfiLendingFormData.moratoriumDuration = 0
        this.pfiLendingFormData.moratoriumFrequency = ''
      }
    },
    repaymentScheduleCurrentPage(newValue) {
      this.repaymentSchedulePagination.currentPage = newValue
    },
    repaymentSchedulePerPage(newValue) {
      this.repaymentSchedulePagination.perPage = newValue
    },
  },
  mounted() {
    const { type, cat } = this.$route.params
    this.actionType = type
    this.actionCategory = cat
    this.getPermission()
    this.fetchLoanProducts()
    this.getPfiLoanAccountDetails()
    this.fetchBanks()
    this.fetchCollateralInformation()
    this.fetchAllPartnerCodes()
    this.fetchInterestInformation()
  },
  methods: {
    ...mapActions({
      getBankList: 'eChannels/getAllBankList',
      getProduct: 'account/getProducts',
      getPfiLoanAccount: 'account/getPfiLoanAccount',
      getComputedInterest: 'account/getComputedInterest',
      getPaymentSchedule: 'account/ScheduleSimulation',
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      deleteDocumentsUpload: 'account/deleteDocumentsUploadAction',
      saveProgressPfiLendingAccount: 'account/saveProgressPfiLendingAccount',
      // submitPfiLendingAccount: 'account/submitPfiLendingAccount',
      getPFIDetails: 'account/getPFIDetailsAction',
      getAvailableCollaterals: 'account/getAvailableCollaterals',
      getCollateralDetails: 'account/getCollateralDetails',
      getPfiByPartnerCode: 'account/getPfiByPartnerCode',
      getAllPartnerCodes: 'account/getAllPartnerCodes',
      downloadLoanApplicationLetter: 'reports/downloadLoanApplicationLetter',
      getInterestInformation: 'operations/getInterestInformationList',
      validateLoanAmount: 'account/validateLoanAmount',
    }),
    resetValidationModal() {
      this.allValidationErrors = []
    },
    prevTab() {
      this.$refs.pfiLending.$on('on-change', tabIndex => {
        this.activateTabIndex = tabIndex - 1
      })
      this.$refs.pfiLending.prevTab()
    },
    nextTab() {
      this.$refs.pfiLending.$on('on-change', tabIndex => {
        this.activateTabIndex = tabIndex + 1
      })
      this.$refs.pfiLending.nextTab()
    },
    computeTenure1(value) {
      this.pfiLendingFormData.principalToBeRepaidEvery = value
      if (this.pfiLendingFormData.principalToBeRepaidEvery && this.pfiLendingFormData.principalFrequency && this.pfiLendingFormData.principalInstallment) {
        let frequency
        if (this.pfiLendingFormData.principalFrequency === 'Monthly') {
          frequency = 30
        }
        if (this.pfiLendingFormData.principalFrequency === 'Quarterly') {
          frequency = 90
        }
        if (this.pfiLendingFormData.principalFrequency === 'HalfYearly') {
          frequency = 180
        }
        if (this.pfiLendingFormData.principalFrequency === 'Annually') {
          frequency = this.annualPrincipalFrequency
        }
        this.pfiLendingFormData.tenure = this.pfiLendingFormData.principalToBeRepaidEvery * frequency * this.pfiLendingFormData.principalInstallment
      }
    },
    computeTenure2(value) {
      this.pfiLendingFormData.principalFrequency = value
      if (this.pfiLendingFormData.principalToBeRepaidEvery && this.pfiLendingFormData.principalFrequency && this.pfiLendingFormData.principalInstallment) {
        let frequency
        if (this.pfiLendingFormData.principalFrequency === 'Monthly') {
          frequency = 30
        }
        if (this.pfiLendingFormData.principalFrequency === 'Quarterly') {
          frequency = 90
        }
        if (this.pfiLendingFormData.principalFrequency === 'HalfYearly') {
          frequency = 180
        }
        if (this.pfiLendingFormData.principalFrequency === 'Annually') {
          frequency = this.annualPrincipalFrequency
        }
        this.pfiLendingFormData.tenure = this.pfiLendingFormData.principalToBeRepaidEvery * frequency * this.pfiLendingFormData.principalInstallment
      }
    },
    computeTenure3(value) {
      this.pfiLendingFormData.principalInstallment = value
      if (this.pfiLendingFormData.principalToBeRepaidEvery && this.pfiLendingFormData.principalFrequency && this.pfiLendingFormData.principalInstallment) {
        let frequency
        if (this.pfiLendingFormData.principalFrequency === 'Monthly') {
          frequency = 30
        }
        if (this.pfiLendingFormData.principalFrequency === 'Quarterly') {
          frequency = 90
        }
        if (this.pfiLendingFormData.principalFrequency === 'HalfYearly') {
          frequency = 180
        }
        if (this.pfiLendingFormData.principalFrequency === 'Annually') {
          frequency = this.annualPrincipalFrequency
        }
        this.pfiLendingFormData.tenure = this.pfiLendingFormData.principalToBeRepaidEvery * frequency * this.pfiLendingFormData.principalInstallment
      }
    },
    async fetchInterestInformation() {
      try {
        const response = await this.getInterestInformation({
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.annualPrincipalFrequency = data.data.numberOfAccruingInterestDaysInAYear
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAllPartnerCodes() {
      try {
        this.documentIdentifier = uuidv4()
        const response = await this.getAllPartnerCodes({
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.pfiPartnerOptions = data.data.map(item => ({ label: `${item.institutionName} - ${item.partnerCode}`, value: item.partnerCode }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async downloadLoanDocumentTemplate(partnerCode, route) {
      const templateConfigs = {
        GetLoanApplicationDeferralTemplate: {
          title: 'Loan Application Letter Template',
          filename: 'Loan_Application_Letter_Template.docx',
        },
        GetSupplementialFinancingAgreementTemplate: {
          title: 'Supplementary Financing Agreement Template',
          filename: 'Supplementary_Financing_Agreement.docx',
        },

        default: {
          title: 'Environmental And Social Commitment Letter Template',
          filename: 'Environmental_And_Social_Commitment_Letter.docx',
        },
      }

      try {
        this.isBusy = true

        const response = await this.downloadLoanApplicationLetter({
          partnerCode,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
          route,
        })

        if (response.status === 200) {
          const config = templateConfigs[route] || templateConfigs.default

          const blob = new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          )

          FileSaver.saveAs(blob, config.filename)

          this.alertSuccess({
            text: `Your request to download ${config.title} was successful`,
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    addNewDocument() {
      this.documentsFormData.push({
        type: '',
        url: null,
        file: [],
        loading: false,
        title: 'Others',
      })
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({
        text: 'Your request to download file was successful',
      })
    },
    async removeDocument(id, index) {
      const item = this.documentsFormData[index]
      try {
        item.loading = true
        this.documentLoadSpinner = true
        const formData = new FormData()
        formData.append('id', id)
        const response = await this.deleteDocumentsUpload({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { status } = response
        if (status === 200) {
          this.documentsFormData.splice(index, 1)
          this.alertSuccess({ text: 'Document Removed' })
          item.loading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Delete failed, Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        item.loading = false
      }
    },
    async onDocumentsUpload(index, event) {
      const item = this.documentsFormData[index]

      let docTitle
      if (item.title === 'Loan Application Letter') {
        docTitle = 'LoanApplicationForm'
      } else if (item.title === 'Supplementary Financing Agreement') {
        docTitle = 'SupplementaryFinancingAgreement'
      } else if (item.title === 'Environmental & Social Commitment Letter') {
        docTitle = 'EnvironmentalAndSocialCommitmentLetter'
      } else {
        docTitle = 'Others'
      }

      try {
        item.loading = true
        const { files } = event.target

        item.validationError = ''

        if (!files[0]) {
          item.validationError = 'Please select a file.'
          return
        }

        const validTypes = [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'image/bmp',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ]

        if (!validTypes.includes(files[0].type)) {
          item.validationError = 'Unsupported file type.'
          item.loading = false
          return
        }

        const maxSizeMB = 5 // 5 MB
        if (files[0].size > maxSizeMB * 1024 * 1024) {
          item.validationError = `File size exceeds ${maxSizeMB}MB limit.`
          item.loading = false
          return
        }
        const formData = new FormData()
        formData.append('attachedFile', files[0])
        formData.append('type', docTitle)
        formData.append('referenceNumber', this.documentIdentifier)
        const response = await this.uploadDocumentImages({ formData, permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT })
        const { status, data } = await response
        if (status === 200 && data) {
          item.url = data.url
          item.id = data.id
          item.fileExtension = data.fileExtension
          item.loading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to upload image failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        item.loading = false
      }
    },
    async fetchCollateralInformation() {
      try {
        const response = await this.getAvailableCollaterals({
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          // eslint-disable-next-line no-nested-ternary
          this.collateralTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: addSpaceToString(item.collateralType), value: item.referenceCode })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async setCollateralInformation(referenceCode) {
      try {
        const response = await this.getCollateralDetails({
          referenceCode,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.pfiCollateralData = {
            collateralValuation: toBalance(data.data[0].collateralValuation),
            collateralDescription: data.data[0].collateralDescription,
            location: data.data[0].location,
            currencyCode: data.data[0].currencyCode,
            perfectionStatus: data.data[0].perfectionStatus,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    showSearchField() {
      this.resetFormData()
    },
    getPageNumber(index) {
      return (this.repaymentSchedulePagination.currentPage * this.repaymentSchedulePagination.perPage) - this.repaymentSchedulePagination.perPage + index + 1
    },
    async fetchBanks() {
      try {
        this.isBusy = true
        const response = await this.getBankList({ permission: this.permission })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.bankListOptions = data.data.map(item => ({
            label: `${item.name}`,
            value: item.identifier,
          })).sort((a, b) => a.label.localeCompare(b.label))
          this.isBusy = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    setMoratorium(selectedValue) {
      if (selectedValue === 'False') {
        this.isAllowMoratorium = false
        this.moratoriumEffect = null
        this.moratoriumDuration = null
        this.moratoriumFrequency = null
      } else {
        this.isAllowMoratorium = true
      }
    },
    getPermission() {
      if (this.actionType === 'add') {
        this.permission = lmsPermissions.ADD_EXTERNAL_LOAN_ACCOUNT
        return true
      }
      if (this.actionType === 'edit') {
        this.permission = lmsPermissions.MODIFY_EXTERNAL_LOAN_ACCOUNT
        return true
      }
      this.permission = lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT
      return true
    },
    computeTenure({ principalFrequency, principalInstallment, principalToBeRepaidEvery }) {
      let frequency
      if (principalFrequency === 'Daily') {
        frequency = 1
      }
      if (principalFrequency === 'Weekly') {
        frequency = 7
      }
      if (principalFrequency === 'Monthly') {
        frequency = 30
      }
      if (principalFrequency === 'Annually') {
        frequency = this.annualPrincipalFrequency
      }

      this.pfiLendingFormData.tenure = principalToBeRepaidEvery * frequency * principalInstallment
    },
    setEndDate() {
      const { effectiveDate, tenure } = this.pfiLendingFormData
      if (effectiveDate && tenure) {
        const [day, month, year] = effectiveDate.split('/')
        const startDate = new Date(`${year}-${month}-${day}`)

        const maturityDateObj = new Date(startDate)
        maturityDateObj.setDate(startDate.getDate() + tenure)

        this.pfiLendingFormData.maturityDate = maturityDateObj.toLocaleDateString('en-GB')
      }
    },
    csvExport() {
      const arrData = this.newRepaymentSchedules.map(item => ({
        'Repayment Date': item.paymentDate,
        'Loan Account Number': item.loanAccountNumber,
        Principal: item.principal,
        Interest: item.interest,
        'Loan Fee': item.fee,
        'Total Repayment': item.totalRepayment,
      }))
      lmsExportToCSV({
        arrData, institutionName: this.pfiCustomerFormData.partnerName, startDate: this.pfiLendingFormData.effectiveDate, fileName: 'Repayment Schedule',
      })
    },
    excelExport(arrData) {
      lmsExportToExcel({
        arrData, institutionName: this.pfiCustomerFormData.partnerName, startDate: this.pfiLendingFormData.effectiveDate, fileName: 'Repayment Schedule',
      })
    },

    pdfExport() {
      const columns = [
        { title: this.$t('Repayment Date'), key: 'paymentDate' },
        { title: this.$t('Loan Account Number'), key: 'loanAccountNumber' },
        { title: this.$t('Principal'), key: 'principal' },
        { title: this.$t('Interest'), key: 'interest' },
        { title: this.$t('Loan Fee'), key: 'fee' },
        { title: this.$t('Total Repayment'), key: 'totalRepayment' },
      ]
      lmsExportToPDF({
        arrData: this.repaymentSchedules,
        columns,
        institutionName: this.pfiCustomerFormData.partnerName,
        startDate: this.pfiLendingFormData.effectiveDate,
        fileName: `${this.$t('Repayment Schedule')}.pdf`,
      })
    },
    async setLoanFee(selectedValue) {
      try {
        const selectedLoanProduct = this.loanProductOptions.find(item => item.value === selectedValue)
        this.pfiLendingFormData.loanProductName = selectedLoanProduct ? selectedLoanProduct.label : ''

        const response = await this.getProduct({
          search: { code: selectedValue },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200) {
          const [loanProduct] = data.data
          this.pfiLendingFormData.defaultingInterestRate = loanProduct.defaultingInterestPercentage
          this.pfiLendingFormData.tenure = loanProduct.tenure
          this.pfiLendingFormData.interestRate = loanProduct.interestRate
          this.pfiLendingFormData.penaltyGracePeriod = loanProduct.penaltyGracePeriod
          this.pfiLendingFormData.moratoriumDuration = loanProduct.moratoriumDuration
          this.pfiLendingFormData.moratoriumEffect = loanProduct.moratoriumEffect
          this.pfiLendingFormData.moratoriumFrequency = loanProduct.moratoriumFrequency
          this.pfiLendingFormData.allowMoratorium = loanProduct.allowMoratorium
          this.setMoratorium(loanProduct.allowMoratorium)
          this.pfiLendingFormData.enableRepaymentTracker = loanProduct.enableRepaymentTracker
          this.pfiLendingFormData.repaymentTrackerInDays = loanProduct.repaymentTrackerInDays
          this.pfiLendingFormData.principalRepaymentType = loanProduct.principalRepaymentType
          this.pfiLendingFormData.principalToBeRepaidEvery = loanProduct.principalToBeRepaidEvery
          this.pfiLendingFormData.principalFrequency = loanProduct.principalFrequency
          this.pfiLendingFormData.principalInstallment = loanProduct.principalInstallment
          this.pfiLendingFormData.interestRepaymentType = loanProduct.interestRepaymentType
          this.pfiLendingFormData.interestToBeRepaidEvery = loanProduct.interestToBeRepaidEvery
          this.pfiLendingFormData.interestFrequency = loanProduct.interestFrequency
          this.pfiLendingFormData.interestInstallment = loanProduct.interestInstallment
          this.pfiLendingFormData.applyLoanFees = loanProduct.applyLoanFees
          this.pfiLendingFormData.interestAccrualMode = loanProduct.interestAccrualMode
          this.pfiLendingFormData.isAllowDisbursementsToGL = loanProduct.allowDisbursementsToGL
          this.pfiLendingFormData.currencyCode = loanProduct.currencyCode

          // if (loanProduct.allowMoratorium !== 'False') {
          //   this.isIgnoreProductDefault = true
          // }

          this.computeTenure({
            principalFrequency: loanProduct.principalFrequency,
            principalInstallment: loanProduct.principalInstallment,
            principalToBeRepaidEvery: loanProduct.principalToBeRepaidEvery,
          })
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async fetchLoanProducts() {
      try {
        this.isBusy = true
        const response = await this.getProduct({
          search: { type: 'Loan' },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200) {
          this.loanProductOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
      return true
    },
    async computeInterest() {
      try {
        const response = await this.getComputedInterest({
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
          formData: {
            accountNumber: this.pfiLendingFormData.externalDisbursementAccount,
            name: this.pfiLendingFormData.partnerName,
            productCode: this.pfiLendingFormData.externalProductCode,
            linkedNumber: this.pfiLendingFormData.externalDisbursementAccount,
            principalToBeRepaidEvery: this.pfiLendingFormData.principalToBeRepaidEvery || 0,
            principalInstallment: this.pfiLendingFormData.principalInstallment,
            interestToBeRepaidEvery: this.pfiLendingFormData.interestToBeRepaidEvery || 0,
            interestInstallment: this.pfiLendingFormData.interestInstallment || 0,
            loanAmount: toKobo(this.pfiLendingFormData.amount),
            startDate: toYearWithDate(this.pfiLendingFormData.effectiveDate),
            endDate: toYearWithDate(this.pfiLendingFormData.maturityDate),
            principalRepaymentType: this.pfiLendingFormData.principalRepaymentType,
            principalFrequency: this.pfiLendingFormData.principalFrequency,
            defaultingInterestRate: this.pfiLendingFormData.defaultingInterestRate,
            interestRepaymentType: this.pfiLendingFormData.interestRepaymentType,
            interestFrequency: this.pfiLendingFormData.interestFrequency,
            interestAccrualMode: this.pfiLendingFormData.interestAccrualMode,
            allowMoratorium: this.pfiLendingFormData.allowMoratorium,
            moratoriumEffect: this.pfiLendingFormData.moratoriumEffect,
            moratoriumDuration: this.pfiLendingFormData.moratoriumDuration || 0,
            moratoriumFrequency: this.pfiLendingFormData.moratoriumFrequency,
          },
        })
        const totalInterestAmount = `${this.$t('Total Interest Amount')} = ${toBalance(response.data.totalInterestAmount)}`
        const totalDays = `${this.$t('Total Days')} = ${response.data.totalDays}`
        this.computedInterest = `${totalInterestAmount} \n ${totalDays}`

        this.showSpinner = false
        this.showComputedInterest = true
        // }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    async onViewRepaymentSchedule() {
      try {
        this.showSpinner = true
        const response = await this.getPaymentSchedule({
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
          formData: {
            loanAmount: toKobo(this.pfiLendingFormData.amount),
            startDate: toYearWithDate(this.pfiLendingFormData.effectiveDate),
            tenure: this.pfiLendingFormData.tenure,
            principalFrequency: this.pfiLendingFormData.principalFrequency,
            interestFrequency: this.pfiLendingFormData.interestFrequency,
            applicableInterestRate: this.pfiLendingFormData.interestRate,
            principalInstallment: this.pfiLendingFormData.principalInstallment,
            principalToBeRepaidEvery: this.pfiLendingFormData.principalToBeRepaidEvery,
            productCode: this.pfiLendingFormData.externalProductCode,
            moratoriumDuration: this.pfiLendingFormData.moratoriumDuration || 0,
            moratoriumFrequency: this.pfiLendingFormData.moratoriumFrequency,
          },
        })
        const { status, data } = response
        if (status === 200) {
          this.repaymentSchedules = data.responseResult.data.map(item => ({
            ...item,
            fee: addNumberSeparator(item.fee),
            interest: addNumberSeparator(item.interest),
            paymentDate: toDateWithMonth(item.paymentDate),
            principal: addNumberSeparator(item.principal),
            totalRepayment: addNumberSeparator(item.totalRepayment),
          }))
          this.newRepaymentSchedules = data.responseResult.data.map(item => ({
            ...item,
            fee: item.fee,
            interest: item.interest,
            paymentDate: toDateWithMonth(item.paymentDate),
            principal: item.principal,
            totalRepayment: item.totalRepayment,
          }))
          this.repaymentSchedulePagination.totalEntries = data.responseResult.recordCount
          this.showSpinner = false
          this.$root.$emit('bv::show::modal', 'repayment-schedule-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    setCollateral(value) {
      if (!value) {
        this.pfiLendingFormData.collateralType = null
        this.pfiLendingFormData.collateralValuation = null
        this.pfiLendingFormData.collateralDescription = null
        return false
      }
      return true
    },

    async handleSaveProgress() {
      this.confirm(this.saveProgressLoanAccount, { title: '', text: this.$t('Are you sure you want to Save and Exit this Loan?') })
    },
    async saveProgressLoanAccount() {
      try {
        this.isLoading = true
        this.isDisabled = true

        const formData = {
          partnerCode: this.pfiCustomerFormData.partnerCode,
          description: this.pfiLendingFormData.description,
          externalProductCode: this.pfiLendingFormData.externalProductCode,
          externalBankName: this.pfiLendingFormData.externalBankName,
          externalDisbursementAccount: this.pfiLendingFormData.externalDisbursementAccount,
          amount: toKobo(this.pfiLendingFormData.amount),
          tenure: this.pfiLendingFormData.tenure,
          principalRepaymentType: this.pfiLendingFormData.principalRepaymentType,
          principalFrequency: this.pfiLendingFormData.principalFrequency,
          interestRepaymentType: this.pfiLendingFormData.interestRepaymentType,
          interestFrequency: this.pfiLendingFormData.interestFrequency,
          interestInstallment: this.pfiLendingFormData.interestInstallment,
          interestAccrualMode: this.pfiLendingFormData.interestAccrualMode,
          collateralCode: this.pfiLendingFormData.collateralCode,
          effectiveDate: this.pfiLendingFormData.effectiveDate ? toYearWithDate(this.pfiLendingFormData.effectiveDate) : null,
          maturityDate: this.pfiLendingFormData.maturityDate ? toYearWithDate(this.pfiLendingFormData.maturityDate) : null,
          principalToBeRepaidEvery: this.pfiLendingFormData.principalToBeRepaidEvery,
          principalInstallment: this.pfiLendingFormData.principalInstallment,
          defaultingInterestRate: this.pfiLendingFormData.defaultingInterestRate || 0,
          interestRate: this.pfiLendingFormData.interestRate,
          interestToBeRepaidEvery: this.pfiLendingFormData.interestToBeRepaidEvery,
          allowMoratorium: this.pfiLendingFormData.allowMoratorium,
          moratoriumEffect: this.pfiLendingFormData.moratoriumEffect,
          moratoriumDuration: this.pfiLendingFormData.moratoriumDuration || 0,
          moratoriumFrequency: this.pfiLendingFormData.moratoriumFrequency,
          documentIdentifier: this.documentIdentifier,
          hasEndBorrowersList: this.pfiLendingFormData.hasEndBorrowersList,
          deadlineForEndBorrowersList: this.pfiLendingFormData.deadlineForEndBorrowersList ? toYearWithDate(this.pfiLendingFormData.deadlineForEndBorrowersList) : null,
          initiatedBy: this.userDetails.email,
          id: this.pfiLendingFormData.id,
        }
        const response = this.saveProgressPfiLendingAccount({ formData, permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT })
        const { status } = await response
        if (status === 200) {
          this.alertSuccess({ text: this.$t('Your request to Save was successful') })

          this.$router.push({ name: 'loan-application-list' })
        }
      } catch (error) {
        this.isLoading = false
        this.isDisabled = false
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    setTermsAndConditions(checkValue) {
      if (!checkValue) {
        this.isSubmitDisabled = true
        this.customErrors.agreeToTerms = 'You must agree to the terms and conditions'
      } else {
        this.isSubmitDisabled = false
        this.customErrors.agreeToTerms = ''
      }
    },
    async handleSubmit() {
      const valid = this.pfiLendingFormData.hasCheckedAcceptanceForm
      if (valid) {
        this.confirm(this.loanAccountAction, { title: '', text: `${this.$t('Are you sure you want to')} ${this.actionType === 'add' ? 'Save' : 'Modify'} this Loan?` })
      } else {
        this.customErrors.agreeToTerms = 'You must agree to the terms and conditions'
      }
    },
    async loanAccountAction() {
      try {
        this.isSubmitLoading = true
        this.isSubmitDisabled = false

        const formData = {
          partnerCode: this.pfiCustomerFormData.partnerCode,
          description: this.pfiLendingFormData.description,
          externalProductCode: this.pfiLendingFormData.externalProductCode,
          externalBankName: this.pfiLendingFormData.externalBankName,
          externalDisbursementAccount: this.pfiLendingFormData.externalDisbursementAccount,
          amount: toKobo(this.pfiLendingFormData.amount),
          tenure: this.pfiLendingFormData.tenure,
          principalRepaymentType: this.pfiLendingFormData.principalRepaymentType,
          principalFrequency: this.pfiLendingFormData.principalFrequency,
          interestRepaymentType: this.pfiLendingFormData.interestRepaymentType,
          interestFrequency: this.pfiLendingFormData.interestFrequency,
          interestInstallment: this.pfiLendingFormData.interestInstallment,
          interestAccrualMode: this.pfiLendingFormData.interestAccrualMode,
          collateralCode: this.pfiLendingFormData.collateralCode,
          effectiveDate: this.pfiLendingFormData.effectiveDate ? toYearWithDate(this.pfiLendingFormData.effectiveDate) : null,
          maturityDate: this.pfiLendingFormData.maturityDate ? toYearWithDate(this.pfiLendingFormData.maturityDate) : null,
          principalToBeRepaidEvery: this.pfiLendingFormData.principalToBeRepaidEvery,
          principalInstallment: this.pfiLendingFormData.principalInstallment,
          defaultingInterestRate: this.pfiLendingFormData.defaultingInterestRate || 0,
          interestRate: this.pfiLendingFormData.interestRate,
          interestToBeRepaidEvery: this.pfiLendingFormData.interestToBeRepaidEvery,
          allowMoratorium: this.pfiLendingFormData.allowMoratorium,
          moratoriumEffect: this.pfiLendingFormData.moratoriumEffect,
          moratoriumDuration: this.pfiLendingFormData.moratoriumDuration || 0,
          moratoriumFrequency: this.pfiLendingFormData.moratoriumFrequency,
          hasCheckedAcceptanceForm: this.pfiLendingFormData.hasCheckedAcceptanceForm,
          documentIdentifier: this.documentIdentifier,
          hasEndBorrowersList: this.pfiLendingFormData.hasEndBorrowersList,
          deadlineForEndBorrowersList: this.pfiLendingFormData.deadlineForEndBorrowersList ? toYearWithDate(this.pfiLendingFormData.deadlineForEndBorrowersList) : null,
          applicationStage: 'Submitted',
          initiatedBy: this.userDetails.email,
          id: this.pfiLendingFormData.id,
        }
        const response = await this.saveProgressPfiLendingAccount({ formData, permission: lmsPermissions.ADD_EXTERNAL_LOAN_ACCOUNT })

        const { status } = await response
        if (status === 200) {
          this.alertSuccess({ text: this.$t('Your request to Create Loan account was successful') })

          this.$router.push({ name: 'loan-application-list' })
        }
      } catch (error) {
        this.isSubmitLoading = false
        this.isSubmitDisabled = false
        this.handleError(error)
      } finally {
        this.isSubmitLoading = false
        this.isSubmitDisabled = false
      }
    },

    async getLinkedPfiByPartnerCode(partnerCode) {
      try {
        this.showSpinner = true
        const response = await this.getPfiByPartnerCode({
          partnerCode,
          permission: this.permission,
        })

        const { data, status } = await response
        if (status === 200 && data) {
          const itemValue = data.data[0].backgroundInformation

          this.pfiCustomerFormData = {
            partnerName: itemValue.institutionName,
            partnerCode: data.data[0].partnerCode,
            state: itemValue.state,
            contactEmailAddress: data.data[0].contactInfo[0].emailAddress,
            contactPhoneNumber: data.data[0].contactInfo[0].contactPhoneNumber,
          }
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ text: error })
        this.showSpinner = false
      }
    },
    async getPfiLoanAccountDetails() {
      try {
        const { id, type } = this.$route.params
        if (id !== undefined) {
          this.showSpinner = true
          this.showCustomerInfo = true
          this.showBackButton = true
          this.actionType = type
          this.isDisabled = type !== 'edit'
          this.isEditable = true
          this.isReadonly = type !== 'edit'

          const response = await this.getPfiLoanAccount({ id, permission: this.permission })
          const { data, status } = response
          if (status === 200) {
            await this.getLinkedPfiByPartnerCode(data.data.partnerCode)
            this.setCollateralInformation(data.data.collateralCode)

            this.pfiLendingFormData = {
              ...data.data,
              id: data.data.id,
              partnerName: data.data.partnerName,
              partnerCode: data.data.partnerCode,
              externalProductCode: data.data.externalProductCode,
              amount: toBalance(data.data.amount),
              collateralCode: data.data.collateralCode,
              principalRepaymentType: data.data.principalRepaymentType,
              principalFrequency: data.data.principalFrequency,
              applicableInterestRate: data.data.applicableInterestRate,
              defaultingInterestRate: data.data.defaultingInterestRate || null,
              interestRepaymentType: data.data.interestRepaymentType,
              interestFrequency: data.data.interestFrequency,
              interestAccrualMode: data.data.interestAccrualMode,
              effectiveDate: toDateWithYear(data.data.effectiveDate),
              maturityDate: data.data.maturityDate ? toDateWithYear(data.data.maturityDate) : null,
              principalToBeRepaidEvery: data.data.principalToBeRepaidEvery,
              principalInstallment: data.data.principalInstallment || '0',
              interestToBeRepaidEvery: data.data.interestToBeRepaidEvery,
              interestInstallment: data.data.interestInstallment || '0',
              tenure: data.data.tenure,
              moratoriumDuration: data.data.moratoriumDuration,
              moratoriumEffect: data.data.moratoriumEffect,
              moratoriumFrequency: data.data.moratoriumFrequency,
              allowMoratorium: data.data.allowMoratorium,
              hasEndBorrowersList: data.data.hasEndBorrowersList,
              deadlineForEndBorrowersList: data.data.deadlineForEndBorrowersList ? toDateWithYear(data.data.deadlineForEndBorrowersList) : null,
            }
            this.setMoratorium(data.data.allowMoratorium)

            if (this.actionType === 'edit' && this.actionCategory === 'resume') {
              let applicationStage
              if (data.data.applicationStage === 'Draft') {
                applicationStage = 5
              }
              this.$nextTick(() => {
                if (this.$refs.pfiLending) {
                  this.activateTabIndex = 4
                  this.$refs.pfiLending.activeTabIndex = applicationStage
                  this.$refs.pfiLending.activateTab(applicationStage)
                }
              })
            }

            this.documentIdentifier = data.data.documentIdentifier ? data.data.documentIdentifier : this.documentIdentifier
            this.documentsFormData = data.data.documents ? data.data.documents.map(item => ({
              type: item.type,
              url: item.url,
              file: item.type,
              id: item.id,
              fileExtension: item.fileExtension,
              loading: false,
              title: addSpaceToString(item.type),
            })) : [{
              type: '',
              url: null,
              file: [],
              loading: false,
              validationError: '',
              title: 'Loan Application Letter',
            },
            {
              type: '',
              url: null,
              file: [],
              loading: false,
              validationError: '',
              title: 'Environmental & Social Commitment Letter',
            },
            {
              type: '',
              url: null,
              file: [],
              loading: false,
              validationError: '',
              title: 'Supplementary Financing Agreement',
            },
            ]
            this.showSpinner = false
            this.$refs.loanAccount.activateAll()
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    resetFormData() {
      this.pfiLendingFormData = { ...null }
    },
    backNavigationAction() {
      this.showBackButton = true
      this.$router.push({
        name: 'loan-application-list',
      })
    },
    validateLoanService() {
      return new Promise((resolve, reject) => {
        this.$refs.loanService.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all loanService Errors
            const errorMessages = []
            Object.keys(this.$refs.loanService.errors).forEach(key => {
              const value = this.$refs.loanService.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    validateLoanDisbursement() {
      return new Promise((resolve, reject) => {
        this.$refs.loanDisbursementRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all loanDisbursementRules Errors
            const errorMessages = []
            Object.keys(this.$refs.loanDisbursementRules.errors).forEach(key => {
              const value = this.$refs.loanDisbursementRules.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    validateLoanAccountInfo() {
      return new Promise((resolve, reject) => {
        this.$refs.loanAccountInfo.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all loanAccountInfo Errors
            const errorMessages = []
            Object.keys(this.$refs.loanAccountInfo.errors).forEach(key => {
              const value = this.$refs.loanAccountInfo.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    validateCollateralAndSecurity() {
      return new Promise((resolve, reject) => {
        this.$refs.collateralSecurity.validate().then(async success => {
          if (success) {
            try {
              const formData = {
                partnerCode: this.pfiCustomerFormData.partnerCode,
                description: this.pfiLendingFormData.description,
                externalProductCode: this.pfiLendingFormData.externalProductCode,
                externalBankName: this.pfiLendingFormData.externalBankName,
                externalDisbursementAccount: this.pfiLendingFormData.externalDisbursementAccount,
                amount: toKobo(this.pfiLendingFormData.amount),
                tenure: this.pfiLendingFormData.tenure,
                principalRepaymentType: this.pfiLendingFormData.principalRepaymentType,
                principalFrequency: this.pfiLendingFormData.principalFrequency,
                interestRepaymentType: this.pfiLendingFormData.interestRepaymentType,
                interestFrequency: this.pfiLendingFormData.interestFrequency,
                interestInstallment: this.pfiLendingFormData.interestInstallment,
                interestAccrualMode: this.pfiLendingFormData.interestAccrualMode,
                collateralCode: this.pfiLendingFormData.collateralCode,
                effectiveDate: this.pfiLendingFormData.effectiveDate ? toYearWithDate(this.pfiLendingFormData.effectiveDate) : null,
                maturityDate: this.pfiLendingFormData.maturityDate ? toYearWithDate(this.pfiLendingFormData.maturityDate) : null,
                principalToBeRepaidEvery: this.pfiLendingFormData.principalToBeRepaidEvery,
                principalInstallment: this.pfiLendingFormData.principalInstallment,
                defaultingInterestRate: this.pfiLendingFormData.defaultingInterestRate || 0,
                interestRate: this.pfiLendingFormData.interestRate,
                interestToBeRepaidEvery: this.pfiLendingFormData.interestToBeRepaidEvery,
                allowMoratorium: this.pfiLendingFormData.allowMoratorium,
                moratoriumEffect: this.pfiLendingFormData.moratoriumEffect,
                moratoriumDuration: this.pfiLendingFormData.moratoriumDuration || 0,
                moratoriumFrequency: this.pfiLendingFormData.moratoriumFrequency,
              }
              const response = await this.validateLoanAmount({ formData, permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT })
              const { status } = await response
              if (status === 200) {
                resolve(true)
              }
            } catch (error) {
              this.handleError(error)
              reject()
            }
          } else {
            // Get all collateralSecurity Errors
            const errorMessages = []
            Object.keys(this.$refs.collateralSecurity.errors).forEach(key => {
              const value = this.$refs.collateralSecurity.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    validateEndBeneficiary() {
      return new Promise((resolve, reject) => {
        this.$refs.endBeneficiaryRule.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all endBeneficiaryRule Errors
            const errorMessages = []
            Object.keys(this.$refs.endBeneficiaryRule.errors).forEach(key => {
              const value = this.$refs.endBeneficiaryRule.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    validateLoanDocuments() {
      return new Promise((resolve, reject) => {
        this.$refs.loanDocumentsRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all loanDocumentsRules Errors
            const errorMessages = []
            Object.keys(this.$refs.loanDocumentsRules.errors).forEach(key => {
              const value = this.$refs.loanDocumentsRules.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
    async validateTermsAndConditions() {
      return new Promise((resolve, reject) => {
        this.$refs.termsOfUseRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            // Get all termsOfUseRules Errors
            const errorMessages = []
            Object.keys(this.$refs.termsOfUseRules.errors).forEach(key => {
              const value = this.$refs.termsOfUseRules.errors[key]

              if (Array.isArray(value) && value.length > 0) {
                value.forEach(error => {
                  errorMessages.push(`${error}`)
                })
              }
            })
            this.allValidationErrors = errorMessages
            this.$root.$emit('bv::show::modal', 'all-validated-modal')
            reject()
          }
        })
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.wizard-footer-left {
  float: left;
}

.wizard-footer-right {
  float: right;
}

.wizard-btn {
  background-color: #007bff;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.save-exit-btn {
  background-color: #6c757d;
}

.finish-btn {
  background-color: #28a745;
}

.wizard-btn:hover {
  opacity: 0.9;
}
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 12%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
</style>
<style lang="scss">
  @import '@core/scss/vue/libs/vue-wizard.scss';
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
